import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Box,
  Typography,
  IconButton,
  Autocomplete,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { useNotify, useRefresh } from 'react-admin';
import { tenantService } from '../../services/tenantService';

interface NotificationDialogProps {
  open: boolean;
  onClose: () => void;
  recordId?: string | null;
  isEdit?: boolean;
}

interface User {
  id: number;
  name: string;
  email: string;
}

interface Equipment {
  id: number;
  name: string;
  licensePlate: string;
}

export const NotificationDialog: React.FC<NotificationDialogProps> = ({
  open,
  onClose,
  recordId,
  isEdit = false,
}) => {
  const [formData, setFormData] = useState({
    subject: '',
    message: '',
    linkUrl: '',
    linkText: '',
    userId: null as number | null,
    equipmentId: null as number | null,
    isSystem: true,
  });
  const [users, setUsers] = useState<User[]>([]);
  const [equipment, setEquipment] = useState<Equipment[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingEquipment, setLoadingEquipment] = useState(false);
  const notify = useNotify();
  const refresh = useRefresh();

  // Load users
  useEffect(() => {
    const fetchUsers = async () => {
      setLoadingUsers(true);
      try {
        const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;
        const accessToken = localStorage.getItem('accessToken');
        const tenantCode = tenantService.getCurrentTenantCode();

        const response = await fetch(`${baseUrl}/users`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'x-tenant-code': tenantCode,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch users');
        }

        const data = await response.json();
        setUsers(data.data || data);
      } catch (error) {
        console.error('Error fetching users:', error);
        notify('Failed to load users', { type: 'error' });
      } finally {
        setLoadingUsers(false);
      }
    };

    if (open) {
      fetchUsers();
    }
  }, [open, notify]);

  // Load equipment
  useEffect(() => {
    const fetchEquipment = async () => {
      setLoadingEquipment(true);
      try {
        const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;
        const accessToken = localStorage.getItem('accessToken');
        const tenantCode = tenantService.getCurrentTenantCode();

        const response = await fetch(`${baseUrl}/equipment`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'x-tenant-code': tenantCode,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch equipment');
        }

        const data = await response.json();
        setEquipment(data.data || data);
      } catch (error) {
        console.error('Error fetching equipment:', error);
        notify('Failed to load equipment', { type: 'error' });
      } finally {
        setLoadingEquipment(false);
      }
    };

    if (open) {
      fetchEquipment();
    }
  }, [open, notify]);

  // Load existing notification for edit
  useEffect(() => {
    const fetchNotification = async () => {
      if (!isEdit || !recordId) {return;}

      try {
        const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;
        const accessToken = localStorage.getItem('accessToken');
        const tenantCode = tenantService.getCurrentTenantCode();

        const response = await fetch(`${baseUrl}/notifications/${recordId}`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'x-tenant-code': tenantCode,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch notification');
        }

        const data = await response.json();
        setFormData({
          subject: data.subject || '',
          message: data.message || '',
          linkUrl: data.linkUrl || '',
          linkText: data.linkText || '',
          userId: data.userId || null,
          equipmentId: data.equipmentId || null,
          isSystem: data.isSystem || true,
        });
      } catch (error) {
        console.error('Error fetching notification:', error);
        notify('Failed to load notification', { type: 'error' });
      }
    };

    if (open && isEdit && recordId) {
      fetchNotification();
    }
  }, [open, isEdit, recordId, notify]);

  const handleSubmit = async () => {
    if (!formData.subject || !formData.message) {
      notify('Please fill in all required fields', { type: 'error' });
      return;
    }

    setLoading(true);
    try {
      const baseUrl = import.meta.env.VITE_SIMPLE_REST_URL;
      const accessToken = localStorage.getItem('accessToken');
      const tenantCode = tenantService.getCurrentTenantCode();

      const payload = {
        ...formData,
        linkUrl: formData.linkUrl || null,
        linkText: formData.linkText || null,
      };

      const url = isEdit ? `${baseUrl}/notifications/${recordId}` : `${baseUrl}/notifications`;
      const method = isEdit ? 'PATCH' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${accessToken}`,
          'x-tenant-code': tenantCode,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Failed to ${isEdit ? 'update' : 'create'} notification`);
      }

      notify(`Notification ${isEdit ? 'updated' : 'created'} successfully`, { type: 'success' });
      refresh();
      onClose();
    } catch (error) {
      console.error('Error saving notification:', error);
      notify(`Failed to ${isEdit ? 'update' : 'create'} notification`, { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      subject: '',
      message: '',
      linkUrl: '',
      linkText: '',
      userId: null,
      equipmentId: null,
      isSystem: true,
    });
    onClose();
  };

  const selectedUser = users.find(user => user.id === formData.userId);
  const selectedEquipment = equipment.find(eq => eq.id === formData.equipmentId);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            {isEdit ? 'Edit Notification' : 'Create New Notification'}
          </Typography>
          <IconButton onClick={handleClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 2 }}>
          {/* Subject */}
          <TextField
            label="Subject"
            fullWidth
            required
            value={formData.subject}
            onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
            placeholder="Enter notification subject..."
          />

          {/* Message */}
          <TextField
            label="Message"
            multiline
            rows={4}
            fullWidth
            required
            value={formData.message}
            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
            placeholder="Enter notification message..."
          />

          {/* Link URL */}
          <TextField
            label="Link URL (Optional)"
            fullWidth
            value={formData.linkUrl}
            onChange={(e) => setFormData({ ...formData, linkUrl: e.target.value })}
            placeholder="https://example.com"
          />

          {/* Link Text */}
          <TextField
            label="Link Text (Optional)"
            fullWidth
            value={formData.linkText}
            onChange={(e) => setFormData({ ...formData, linkText: e.target.value })}
            placeholder="Click here to view details"
          />

          {/* Target User Selection */}
          <Autocomplete
            options={users}
            getOptionLabel={(user) => `${user.name} (${user.email})`}
            value={selectedUser || null}
            onChange={(_, newValue) => {
              setFormData({
                ...formData,
                userId: newValue ? newValue.id : null,
              });
            }}
            loading={loadingUsers}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Target User (Optional)"
                fullWidth
                helperText="Leave empty to send to all users"
              />
            )}
            renderOption={(props, user) => (
              <Box component="li" {...props}>
                <Box>
                  <Typography variant="body2" fontWeight="medium">
                    {user.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {user.email}
                  </Typography>
                </Box>
              </Box>
            )}
          />

          {/* Related Equipment Selection */}
          <Autocomplete
            options={equipment}
            getOptionLabel={(eq) => `${eq.name} (${eq.licensePlate})`}
            value={selectedEquipment || null}
            onChange={(_, newValue) => {
              setFormData({
                ...formData,
                equipmentId: newValue ? newValue.id : null,
              });
            }}
            loading={loadingEquipment}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Related Equipment (Optional)"
                fullWidth
                helperText="Link notification to specific equipment"
              />
            )}
            renderOption={(props, eq) => (
              <Box component="li" {...props}>
                <Box>
                  <Typography variant="body2" fontWeight="medium">
                    {eq.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {eq.licensePlate}
                  </Typography>
                </Box>
              </Box>
            )}
          />

          {/* System/Manual Toggle */}
          <FormControlLabel
            control={
              <Switch
                checked={formData.isSystem}
                onChange={(e) => setFormData({ ...formData, isSystem: e.target.checked })}
              />
            }
            label="System Generated"
            sx={{ alignSelf: 'flex-start' }}
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !formData.subject || !formData.message}
        >
          {loading ? 'Saving...' : isEdit ? 'Update Notification' : 'Create Notification'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};